import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Mail, MessageCircle, Instagram } from "lucide-react";
import ContactForm from "../components/ContactForm";
import BookingForm from "../components/BookingForm";
import { SITE } from "../data/content";
import SectionHeading from "../components/SectionHeading";
import useReveal from "../hooks/useReveal";

export default function ContactPage() {
    useReveal();
    const { hash } = useLocation();

    useEffect(() => {
        if (hash) {
            const el = document.getElementById(hash.replace("#", ""));
            if (el) {
                setTimeout(
                    () =>
                        el.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        }),
                    180,
                );
            }
        }
    }, [hash]);

    return (
        <div className="pt-24" data-testid="contact-page">
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                <div className="ssh-reveal">
                    <SectionHeading
                        eyebrow="Begin Your Healing Journey"
                        title="Reach out — we'll meet"
                        titleItalic="you with care."
                        sub="There is no pressure here. Whether you're booking a session, asking a question, or just curious — write a few lines and we'll respond personally."
                    />
                </div>
            </section>

            <section className="bg-[#FAF8F5] border-t border-[#EFEBE4]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-4 ssh-reveal">
                        <p className="ssh-eyebrow">Direct lines</p>
                        <h2 className="mt-5 font-serif font-light tracking-tight text-3xl lg:text-4xl text-[#2C2B29]">
                            Or simply reach out{" "}
                            <em className="italic text-[#A68A6B]">
                                personally.
                            </em>
                        </h2>
                        <ul className="mt-10 space-y-6">
                            <li>
                                <a
                                    href={`mailto:${SITE.email}`}
                                    className="flex items-start gap-4 group"
                                    data-testid="contact-direct-email"
                                >
                                    <Mail
                                        className="h-5 w-5 text-[#A68A6B] mt-1"
                                        strokeWidth={1.5}
                                    />
                                    <div>
                                        <p className="ssh-eyebrow">Email</p>
                                        <p className="mt-2 text-base text-[#2C2B29] group-hover:text-[#A68A6B] transition-colors">
                                            {SITE.email}
                                        </p>
                                    </div>
                                </a>
                            </li>
                            <li>
                                <a
                                    href={`https://wa.me/${SITE.whatsappRaw}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-start gap-4 group"
                                    data-testid="contact-direct-whatsapp"
                                >
                                    <MessageCircle
                                        className="h-5 w-5 text-[#A68A6B] mt-1"
                                        strokeWidth={1.5}
                                    />
                                    <div>
                                        <p className="ssh-eyebrow">WhatsApp</p>
                                        <p className="mt-2 text-base text-[#2C2B29] group-hover:text-[#A68A6B] transition-colors">
                                            {SITE.whatsappDisplay}
                                        </p>
                                    </div>
                                </a>
                            </li>
                            <li>
                                <a
                                    href={SITE.instagramUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-start gap-4 group"
                                    data-testid="contact-direct-instagram"
                                >
                                    <Instagram
                                        className="h-5 w-5 text-[#A68A6B] mt-1"
                                        strokeWidth={1.5}
                                    />
                                    <div>
                                        <p className="ssh-eyebrow">Instagram</p>
                                        <p className="mt-2 text-base text-[#2C2B29] group-hover:text-[#A68A6B] transition-colors">
                                            {SITE.instagramHandle}
                                        </p>
                                    </div>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div className="lg:col-span-8 ssh-reveal">
                        <div className="bg-white border border-[#EFEBE4] p-8 lg:p-12">
                            <p className="ssh-eyebrow">General Inquiry</p>
                            <h3 className="mt-3 font-serif text-3xl text-[#2C2B29]">
                                Send a message
                            </h3>
                            <div className="mt-10">
                                <ContactForm />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-[#EFEBE4]" id="booking">
                <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
                    <div className="ssh-reveal">
                        <SectionHeading
                            eyebrow="Book a Session"
                            title="Reserve your space"
                            titleItalic="for a session."
                            sub="Choose your session, pick a date and time that suits you, and confirm instantly with a secure payment. You'll receive a confirmation email the moment it's done."
                        />
                    </div>
                    <div className="mt-12 bg-white border border-[#dfd9cc] p-8 lg:p-12 ssh-reveal">
                        <BookingForm />
                    </div>
                </div>
            </section>
        </div>
    );
}
